import { useEffect, useRef } from 'react';
import { FileDown, FileText, Loader2, Printer, Radio } from 'lucide-react';
import { useDocxExport } from '@skills/docx-export.jsx';
import { usePdfExport } from '@skills/pdf-export.jsx';
import { preloadLogo } from '@generated/components/logoDocx';
import { RHSR_LOGO, VSR_LOGO } from '@generated/components/AdGuide';
import { buildSceneGuideDocx, cleanLink, dayHeading, sortGuide, venueLine } from '@generated/components/sceneGuideDocx';
import type { SceneScout } from '@generated/components/SceneReport';
import { PRESETS, rangeTitle } from '@generated/components/sceneRange';

const btn = 'inline-flex min-h-11 items-center gap-2 border border-foreground px-4 text-sm font-semibold disabled:opacity-50';

/** Reader-facing "The Scene" listings guide — chronological, ready for the paper. */
type Range = { from: string; to: string };
export function SceneGuide({ s, busy, error, run, range, setRange }: { s: SceneScout | null; busy: boolean; error: string | null; run: () => void; range: Range; setRange: (r: Range) => void }) {
  const docRef = useRef<HTMLDivElement>(null);
  const { exportToDocx, isExporting: wordBusy } = useDocxExport();
  const { exportToPdf, isExporting: pdfBusy } = usePdfExport({ pageSize: 'Letter', orientation: 'portrait', margin: 36 });
  useEffect(() => { void preloadLogo(); }, []);

  const events = s ? sortGuide(s.events) : [];
  // Header/file names follow the range the guide was built for (falls back to the picker).
  const built: Range = s?.from && s?.to ? { from: s.from, to: s.to } : range;
  const title = rangeTitle(built.from, built.to);
  const prepared = `The Scene · ${title}`;
  const fileBase = `the-scene-guide_${built.from}_to_${built.to}`;
  const stale = !!s && (s.from !== range.from || s.to !== range.to);
  const validRange = !!range.from && !!range.to && range.from <= range.to;
  const print = () => {
    const node = docRef.current; if (!node) return;
    const w = window.open('', '_blank', 'width=900,height=1000'); if (!w) { window.print(); return; }
    const styles = [...document.querySelectorAll('style, link[rel="stylesheet"]')].map((x) => x.outerHTML).join('');
    w.document.write(`<!doctype html><html class="${document.documentElement.className}"><head><title>${prepared} · Star-Revue</title>${styles}<style>@page{margin:.5in}body{background:#fff}</style></head><body>${node.outerHTML}</body></html>`);
    w.document.close(); setTimeout(() => { w.focus(); w.print(); }, 600);
  };

  let lastDay = '';
  return (
    <div>
      <div className="mb-4 border-2 border-foreground p-4">
        <div className="text-xs font-bold uppercase tracking-[0.18em]">Issue dates</div>
        <div className="mt-2 flex flex-wrap gap-2">{PRESETS.map((pr) => { const r = pr.range(); const on = r.from === range.from && r.to === range.to; return <button key={pr.label} type="button" aria-pressed={on} onClick={() => setRange(r)} className={`min-h-11 border px-3 text-sm font-semibold ${on ? "border-foreground bg-foreground text-background" : "border-border hover:border-foreground"}`}>{pr.label}</button>; })}</div>
        <div className="mt-3 flex flex-wrap items-end gap-3">
          <label className="text-sm">From<input type="date" value={range.from} max={range.to || undefined} onChange={(e) => setRange({ ...range, from: e.target.value })} className="mt-1 block min-h-11 border border-input bg-background px-2" /></label>
          <label className="text-sm">To<input type="date" value={range.to} min={range.from || undefined} onChange={(e) => setRange({ ...range, to: e.target.value })} className="mt-1 block min-h-11 border border-input bg-background px-2" /></label>
          {validRange && <p className="pb-3 text-sm font-semibold">{rangeTitle(range.from, range.to)}</p>}
        </div>
        {stale && <p className="mt-2 text-sm text-destructive">The dates changed. Click "Build the guide" to refresh the listings for this range.</p>}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={run} disabled={busy || !validRange} className="inline-flex min-h-11 items-center gap-2 bg-primary px-5 text-sm font-semibold text-primary-foreground disabled:opacity-50">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Radio className="h-4 w-4" />}Build the guide</button>
        {s && <>
          <button type="button" className={btn} disabled={wordBusy} onClick={() => void exportToDocx(buildSceneGuideDocx(s.events, prepared), `${fileBase}.docx`)}>{wordBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}Word file</button>
          <button type="button" className={btn} disabled={pdfBusy} onClick={() => void exportToPdf(docRef, `${fileBase}.pdf`)}>{pdfBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}PDF</button>
          <button type="button" className={btn} onClick={print}><Printer className="h-4 w-4" />Print</button>
        </>}
      </div>
      {busy && !s && <p className="mt-4 text-sm text-muted-foreground">Reading about 20 venue websites… this can take up to a minute.</p>}
      {error && <p role="alert" className="mt-4 text-sm text-destructive">{error}</p>}
      {s && (
        <div ref={docRef} className={`mt-6 bg-[hsl(var(--paper))] p-6 sm:p-10 ${busy ? 'opacity-60' : ''}`}>
          <header className="flex items-start justify-between gap-4 border-b-4 border-double border-foreground pb-3">
            <div className="min-w-0">
              <h3 className="font-[family-name:var(--font-heading)] text-5xl font-semibold tracking-tight">The Scene</h3>
              <p className="mt-1 text-sm italic">Underground music, readings, theater &amp; counterculture downtown</p>
              <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{title}</p>
            </div>
            <span className="flex shrink-0 gap-2"><img src={RHSR_LOGO} alt="Red Hook Star-Revue" crossOrigin="anonymous" className="h-14 w-14 object-contain" /><img src={VSR_LOGO} alt="The Village Star-Revue" crossOrigin="anonymous" className="h-14 w-14 object-contain" /></span>
          </header>
          {events.length === 0 && <p className="mt-6 text-sm italic">No events were listed on the venue sites for these dates.</p>}
          <div className="mt-2 sm:columns-2 sm:gap-10">
            {events.map((e) => {
              const day = dayHeading(e.startsAt);
              const showDay = day !== lastDay; lastDay = day;
              return (
                <article key={e.title + e.when + e.venue} className="break-inside-avoid pt-4">
                  {showDay && <h4 className="mb-2 border-b-2 border-foreground pb-1 text-xs font-bold uppercase tracking-[0.18em] text-primary">{day}</h4>}
                  {e.category && <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary">{e.category}</p>}
                  <h5 className="font-[family-name:var(--font-heading)] text-lg font-semibold leading-snug">{e.title}</h5>
                  <p className="text-sm font-semibold">{e.when}</p>
                  <p className="text-sm">{venueLine(e)}</p>
                  {e.cost && <p className="text-sm italic">{e.cost}</p>}
                  {e.readerBlurb && <p className="mt-1 text-sm leading-relaxed">{e.readerBlurb}</p>}
                  {e.link?.startsWith('http') && <p className="mt-0.5 break-all text-xs text-muted-foreground">{cleanLink(e.link)}</p>}
                </article>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
